import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, CardHeader, Col, Form, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useApiStore } from "../../hooks/ApiStoreContext";
import Swal from "sweetalert2";
import Select from "react-select";

function AddCoupon() {
  const apiStore = useApiStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [heading, setHeading] = useState("Add");
  const [users, setUsers] = useState([]);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      couponId: null,
      code: "",
      description: "",
      discountPercent: "",
      expirationDate: new Date(new Date().getTime() + 10 * 24 * 60 * 60 * 1000).toISOString().split("T")[0], //current date plus 10 days in YYYY-MM-DD
      isActive: true,
      isValidForAllUsers: false,
      couponUsers: [],
    },
  });

  const isValidForAllUsers = watch("isValidForAllUsers");

  useEffect(() => {
    const id = location.pathname.split("/")[2];
    if (!id) {
      setHeading("Add");
    } else {
      setHeading("Edit");
      setValue("couponId", id);
      fetchCouponData(id);
    }
    fetchUsers();
  }, [location]);

  const fetchUsers = async () => {
    try {
      const response = await apiStore.get("/users");
      setUsers(response.map((user) => ({ value: user.userId, label: `${user.firstName} ${user.lastName} (${user.contact})` })));
    } catch (error) {
      console.error("There was an error fetching users!", error);
    }
  };

  const fetchCouponData = async (id) => {
    try {
      const response = await apiStore.get(`/coupon/${id}`);
      setValue("code", response.code);
      setValue("description", response.description);
      setValue("discountPercent", response.discountPercent);
      setValue("expirationDate", response.expirationDate.substring(0, 10));
      setValue("isActive", response.isActive);
      setValue("isValidForAllUsers", response.isValidForAllUsers);
      if (response.couponUsers && users.length > 0) {
        setValue(
          "couponUsers",
          response.couponUsers.map((user) => ({ value: user.userId, label: users.find((u) => u.value === user.userId)?.label }))
        );
      } else if (response.couponUsers) {
        // fetch user details for each coupon user
        const couponUsers = await Promise.all(
          response.couponUsers.map(async (user) => {
            const userResponse = await apiStore.get(`/users/${user.userId}`);
            return { value: user.userId, label: `${userResponse.firstName} ${userResponse.lastName} (${userResponse.contact})` };
          })
        );
        setValue("couponUsers", couponUsers);
      } else {
        setValue("couponUsers", []);
      }
    } catch (error) {
      if (error.response.status !== 401) {
        console.error("There was an error fetching the coupon!", error);
      }
    }
  };

  const onSubmit = async (data) => {
    try {
      const response = await apiStore.get(`/Coupon/GetCouponByCode?code=${data.code}`);
      if (!!response.couponId && response.couponId != data.couponId) {
        Swal.fire({ title: "Error", text: "Coupon code already exists.", icon: "error", confirmButtonText: "OK" });
        return;
      }

      if (data.isValidForAllUsers) {
        data.couponUsers = [];
      } else {
        if (data.couponUsers.length === 0) {
          Swal.fire({ title: "Error", text: "Please select at least one user.", icon: "error", confirmButtonText: "OK" });
          return;
        }
        data.couponUsers = data.couponUsers.map((user) => ({ couponUserId: 0, couponId: data.couponId || 0, userId: user.value }));
      }

      if (!data.couponId) {
        await apiStore.post("/coupon", data);
        await Swal.fire({ title: "Success", text: "Coupon saved successfully!", icon: "success", confirmButtonText: "OK" });
      } else {
        await apiStore.put(`/coupon/${data.couponId}`, data);
        await Swal.fire({ title: "Updated", text: "Coupon updated successfully!", icon: "success", confirmButtonText: "OK" });
      }

      navigate("/coupon");
      //handleCancel();
    } catch (error) {
      if (error.response.status !== 401) {
        console.error("There was an error saving the coupon!", error);
        Swal.fire({ title: "Error", text: "There was an error saving the coupon.", icon: "error", confirmButtonText: "OK" });
      }
    }
  };

  const handleCancel = (e) => {
    reset({
      code: "",
      description: "",
      discountPercent: "",
      expirationDate: new Date(new Date().getTime() + 10 * 24 * 60 * 60 * 1000).toISOString().split("T")[0], //current date plus 10 days in YYYY-MM-DD
      isActive: true,
      isValidForAllUsers: false,
      couponUsers: [],
    });
  };

  const handleBack = (e) => {
    e.preventDefault();
    navigate("/coupon");
  };

  return (
    <Row>
      <Col md={12}>
        <Card className="mb-4">
          <CardHeader className="pb-0">
            <Row>
              <Col md={9}>
                <h4>{heading} Coupon Details</h4>
              </Col>
              <Col md={3} className="d-grid d-md-flex justify-content-md-end d-sm-block">
                <Button variant="outline-primary" className="btn-sm" onClick={handleBack}>
                  Back
                </Button>
              </Col>
            </Row>
          </CardHeader>
          <CardBody className="px-0 pt-0 pb-2">
            <Form onSubmit={handleSubmit(onSubmit)} className="p-3">
              <Row className="mb-3">
                <input type="hidden" {...register("couponId")}></input>
                <Form.Group controlId="code" className="col-md-6 col-sm-12">
                  <Form.Label>
                    Coupon code <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control type="text" placeholder="Coupon code" {...register("code", { required: "Coupon code is required." })} />
                  <span className="text-xs text-danger">{errors.code?.message}</span>
                </Form.Group>
                <Form.Group controlId="description" className="col-md-6 col-sm-12">
                  <Form.Label>Description</Form.Label>
                  <Form.Control as="textarea" rows={1} placeholder="Description" {...register("description", { maxLength: { value: 500, message: "Maximum 500 characters allowed." } })} />
                  <span className="text-xs text-danger">{errors.description?.message}</span>
                </Form.Group>
              </Row>
              <Row className="mb-3">
                <Form.Group controlId="discountPercent" className="col-md-6 col-sm-12">
                  <Form.Label>
                    Discount Percent <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="Discount percent"
                    {...register("discountPercent", {
                      required: "Discount percent is required.",
                      min: { value: 0.01, message: "Discount percent must be greater than 0." },
                      max: { value: 100, message: "Discount percent must be less than or equal to 100." },
                      valueAsNumber: true,
                    })}
                  />
                  <span className="text-sm text-danger">{errors.discountPercent?.message}</span>
                </Form.Group>
                <Form.Group controlId="expirationDate" className="col-md-6 col-sm-12">
                  <Form.Label>Expiration Date</Form.Label>
                  <Form.Control type="date" rows={3} placeholder="Expiration Date" {...register("expirationDate", { required: "Expiration date is required." })} />
                  <span className="text-xs text-danger">{errors.expirationDate?.message}</span>
                </Form.Group>
              </Row>
              <Row className="mb-3">
                <Form.Group controlId="isActive" className="col-md-6 col-sm-12">
                  <Form.Label>Is Active</Form.Label>
                  <Form.Check type="switch" {...register("isActive")} />
                </Form.Group>
                <Form.Group controlId="isValidForAllUsers" className="col-md-6 col-sm-12">
                  <Form.Label>Is Valid For All Users</Form.Label>
                  <Form.Check type="switch" {...register("isValidForAllUsers")} />
                </Form.Group>
              </Row>
              {!isValidForAllUsers && (
                <Row className="mb-3">
                  <Form.Group controlId="couponUsers" className="col-md-12 col-sm-12">
                    <Form.Label>Select Users</Form.Label>
                    <Select isMulti options={users} value={watch("couponUsers")} onChange={(selectedOptions) => setValue("couponUsers", selectedOptions)} />
                  </Form.Group>
                </Row>
              )}
              <Row>
                <Col md={12} className="d-grid gap-2 d-md-flex flex-md-row-reverse">
                  <Button variant="primary" className="me-md-2" type="submit">
                    Submit
                  </Button>
                  <Button variant="dark" type="button" onClick={handleCancel}>
                    Cancel
                  </Button>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
}

export default AddCoupon;
